import type { AppSchema, ComponentNode, Page, EventBinding, ActionConfig, StyleConfig } from "@shared/types";
import JSZip from "jszip";
import { saveAs } from "file-saver";

function toPascalCase(input: string): string {
  return input
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");
}

function pageComponentName(page: Page): string {
  return `Page${toPascalCase(page.name || "Untitled")}`;
}

function escapeJsString(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/'/g, "\\'").replace(/\n/g, "\\n");
}

function renderStyle(style: Record<string, unknown> | StyleConfig | undefined): string {
  if (!style) return "";
  const entries = Object.entries(style)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${k}: '${escapeJsString(String(v))}'`)
    .join(", ");
  return entries ? ` style={{${entries}}}` : "";
}

function renderEvents(events: EventBinding[] | undefined): string {
  if (!events || events.length === 0) return "";
  const binding = events.find((e) => e.event === "onClick");
  if (!binding) return "";
  const actions = binding.actions.map(renderAction).join("\n    ");
  return ` onClick={() => {\n    ${actions}\n  }}`;
}

function renderAction(action: ActionConfig): string {
  if (action.type === "navigate") {
    const path = String(action.config.path || "/");
    return `navigate('${escapeJsString(path)}');`;
  }
  if (action.type === "openModal") {
    const title = String(action.config.title || "提示");
    const content = String(action.config.content || "");
    return `window.alert('[${escapeJsString(title)}] ${escapeJsString(content)}');`;
  }
  if (action.type === "submitForm") {
    return `/* submit parent form */`;
  }
  return `/* ${action.type} */`;
}

function renderNode(node: ComponentNode, depth = 1): string {
  const indent = "  ".repeat(depth);
  const { type, props, style, children, events } = node;

  const commonStyle = renderStyle(style);
  const commonEvents = renderEvents(events);

  if (type === "Page") {
    const childJsx = children?.map((c) => renderNode(c, depth + 1)).join("\n") || "";
    return `${indent}<div className="min-h-screen p-6">\n${childJsx}\n${indent}</div>`;
  }

  if (type === "Container") {
    const { direction, gap, padding, background } = props || {};
    const flexDir = direction === "row" ? "row" : "column";
    const containerStyle = [
      `display: 'flex'`,
      `flexDirection: '${flexDir}'`,
      gap ? `gap: '${escapeJsString(String(gap))}'` : "",
      padding ? `padding: '${escapeJsString(String(padding))}'` : "",
      background ? `background: '${escapeJsString(String(background))}'` : "",
    ]
      .filter(Boolean)
      .join(", ");
    const childJsx = children?.map((c) => renderNode(c, depth + 1)).join("\n") || "";
    return `${indent}<div style={{${containerStyle}}}${commonStyle}${commonEvents}>\n${childJsx}\n${indent}</div>`;
  }

  if (type === "Text") {
    const { content, tag, fontSize, color, bold } = props || {};
    const Tag = (tag as string) || "p";
    const textStyle = [
      fontSize ? `fontSize: '${escapeJsString(String(fontSize))}'` : "",
      color ? `color: '${escapeJsString(String(color))}'` : "",
      bold ? `fontWeight: 'bold'` : "",
    ]
      .filter(Boolean)
      .join(", ");
    const styleAttr = textStyle ? ` style={{${textStyle}}}` : "";
    return `${indent}<${Tag}${styleAttr}${commonEvents}>${String(content ?? "")}</${Tag}>`;
  }

  if (type === "Button") {
    const { label } = props || {};
    return `${indent}<button type="button" className="rounded bg-slate-900 px-4 py-2 text-white hover:bg-slate-800"${commonEvents}>${String(label ?? "按钮")}</button>`;
  }

  if (type === "Input") {
    const { label, name, placeholder, type: inputType, required } = props || {};
    return (
      `${indent}<label className="block space-y-1">\n` +
      `${indent}  <span className="text-sm font-medium">${String(label ?? "")}${required ? " *" : ""}</span>\n` +
      `${indent}  <input name="${String(name ?? "")}" type="${String(inputType || "text")}" placeholder="${escapeJsString(String(placeholder ?? ""))}" ${required ? "required " : ""}className="w-full rounded border px-3 py-2" />\n` +
      `${indent}</label>`
    );
  }

  if (type === "Textarea") {
    const { label, name, placeholder, rows, required } = props || {};
    return (
      `${indent}<label className="block space-y-1">\n` +
      `${indent}  <span className="text-sm font-medium">${String(label ?? "")}${required ? " *" : ""}</span>\n` +
      `${indent}  <textarea name="${String(name ?? "")}" rows={${Number(rows || 3)}} placeholder="${escapeJsString(String(placeholder ?? ""))}" ${required ? "required " : ""}className="w-full rounded border px-3 py-2" />\n` +
      `${indent}</label>`
    );
  }

  if (type === "Select") {
    const { label, name, placeholder, options, required } = props || {};
    const opts = ((options || []) as { label: string; value: string }[])
      .map((o) => `${indent}    <option value="${escapeJsString(o.value)}">${escapeJsString(o.label)}</option>`)
      .join("\n");
    return (
      `${indent}<label className="block space-y-1">\n` +
      `${indent}  <span className="text-sm font-medium">${String(label ?? "")}${required ? " *" : ""}</span>\n` +
      `${indent}  <select name="${String(name ?? "")}" ${required ? "required " : ""}className="w-full rounded border px-3 py-2">\n` +
      `${indent}    <option value="">${escapeJsString(String(placeholder ?? "请选择"))}</option>\n${opts}\n` +
      `${indent}  </select>\n` +
      `${indent}</label>`
    );
  }

  if (type === "Switch" || type === "Checkbox") {
    const { label, name, required } = props || {};
    return (
      `${indent}<label className="flex items-center gap-2">\n` +
      `${indent}  <input name="${String(name ?? "")}" type="checkbox" ${required ? "required " : ""}className="h-4 w-4" />\n` +
      `${indent}  <span className="text-sm font-medium">${String(label ?? "")}${required ? " *" : ""}</span>\n` +
      `${indent}</label>`
    );
  }

  if (type === "Radio") {
    const { label, name, options, required } = props || {};
    const radios = ((options || []) as { label: string; value: string }[])
      .map(
        (o) =>
          `${indent}  <label key="${escapeJsString(o.value)}" className="flex items-center gap-2">\n` +
          `${indent}    <input type="radio" name="${String(name ?? "")}" value="${escapeJsString(o.value)}" ${required ? "required " : ""} />\n` +
          `${indent}    <span className="text-sm">${escapeJsString(o.label)}</span>\n` +
          `${indent}  </label>`,
      )
      .join("\n");
    return (
      `${indent}<div className="space-y-1">\n` +
      `${indent}  <span className="text-sm font-medium">${String(label ?? "")}${required ? " *" : ""}</span>\n${radios}\n` +
      `${indent}</div>`
    );
  }

  if (type === "DatePicker") {
    const { label, name, required } = props || {};
    return (
      `${indent}<label className="block space-y-1">\n` +
      `${indent}  <span className="text-sm font-medium">${String(label ?? "")}${required ? " *" : ""}</span>\n` +
      `${indent}  <input name="${String(name ?? "")}" type="date" ${required ? "required " : ""}className="w-full rounded border px-3 py-2" />\n` +
      `${indent}</label>`
    );
  }

  if (type === "Table") {
    const { resource, columns } = props || {};
    const cols = ((columns || []) as { field: string; title: string }[])
      .map((c) => `${indent}          <th className="border-b px-4 py-2 text-left">${escapeJsString(c.title || c.field)}</th>`)
      .join("\n");
    const cells = ((columns || []) as { field: string }[])
      .map((c) => `${indent}            <td className="border-b px-4 py-2">{String((row as Record<string, unknown>).${c.field} ?? "")}</td>`)
      .join("\n");
    return (
      `${indent}<div className="rounded border">\n` +
      `${indent}  <div className="border-b bg-gray-100 px-4 py-2 text-sm font-medium">数据表：${String(resource ?? "未配置")}</div>\n` +
      `${indent}  <table className="w-full text-sm">\n` +
      `${indent}    <thead>\n` +
      `${indent}      <tr>\n${cols}\n${indent}      </tr>\n` +
      `${indent}    </thead>\n` +
      `${indent}    <tbody>\n` +
      `${indent}      {(mockData['${String(resource ?? "")}'] || []).map((row, idx) => (\n` +
      `${indent}        <tr key={idx}>\n${cells}\n${indent}        </tr>\n` +
      `${indent}      ))}\n` +
      `${indent}    </tbody>\n` +
      `${indent}  </table>\n` +
      `${indent}</div>`
    );
  }

  if (type === "Form") {
    const { resource, submitLabel } = props || {};
    const childJsx = children?.map((c) => renderNode(c, depth + 1)).join("\n") || "";
    return (
      `${indent}<form\n` +
      `${indent}  className="space-y-4 rounded border p-4"\n` +
      `${indent}  onSubmit={(e) => {\n` +
      `${indent}    e.preventDefault();\n` +
      `${indent}    const formData = new FormData(e.currentTarget);\n` +
      `${indent}    const values = Object.fromEntries(formData.entries());\n` +
      `${indent}    console.log('提交到 ${String(resource ?? "")}:', values);\n` +
      `${indent}    window.alert('提交成功：' + JSON.stringify(values));\n` +
      `${indent}  }}\n` +
      `${indent}>\n${childJsx}\n${indent}  <button type="submit" className="rounded bg-slate-900 px-4 py-2 text-white hover:bg-slate-800">${String(submitLabel ?? "提交")}</button>\n${indent}</form>`
    );
  }

  if (type === "Card") {
    const { title } = props || {};
    const childJsx = children?.map((c) => renderNode(c, depth + 1)).join("\n") || "";
    return (
      `${indent}<div className="rounded border bg-white p-4 shadow-sm"${commonStyle}>\n` +
      (title ? `${indent}  <h3 className="mb-3 text-base font-semibold">${String(title)}</h3>\n` : "") +
      `${childJsx}\n` +
      `${indent}</div>`
    );
  }

  if (type === "AIChat" || type === "Chart") {
    return `${indent}<div className="rounded border bg-gray-50 p-4 text-sm text-gray-500"${commonStyle}${commonEvents}>[${type}] 占位组件</div>`;
  }

  if (type === "RichText") {
    const { content } = props || {};
    return `${indent}<div className="prose prose-sm max-w-none"${commonStyle}${commonEvents} dangerouslySetInnerHTML={{ __html: '${escapeJsString(String(content ?? ""))}' }} />`;
  }

  if (type === "FileUpload") {
    const { target, accept } = props || {};
    return (
      `${indent}<div className="rounded border p-4"${commonStyle}${commonEvents}>\n` +
      `${indent}  <label className="text-sm font-medium">${target === "knowledge" ? "上传知识库文档" : "上传文件"}</label>\n` +
      `${indent}  <input type="file" accept="${escapeJsString(String(accept || ".txt,.md"))}" className="mt-2 block text-sm" />\n` +
      `${indent}</div>`
    );
  }

  // Fallback for unknown components
  const childJsx = children?.map((c) => renderNode(c, depth + 1)).join("\n") || "";
  return `${indent}<div${commonStyle}${commonEvents}>${childJsx}</div>`;
}

function usesMockData(node: ComponentNode): boolean {
  if (node.type === "Table") return true;
  return node.children?.some(usesMockData) ?? false;
}

function usesNavigate(node: ComponentNode): boolean {
  if (node.events?.some((e) => e.actions.some((a) => a.type === "navigate"))) return true;
  return node.children?.some(usesNavigate) ?? false;
}

function generatePageComponent(page: Page): string {
  const name = pageComponentName(page);
  const body = renderNode(page.layout, 1);
  const imports: string[] = [];
  if (usesNavigate(page.layout)) {
    imports.push('import { useNavigate } from "react-router-dom";');
  }
  if (usesMockData(page.layout)) {
    imports.push('import { mockData } from "../data/mock";');
  }

  const hookLine = usesNavigate(page.layout) ? "  const navigate = useNavigate();\n" : "";

  return `${imports.join("\n")}

export function ${name}() {
${hookLine}  return (${body}
  );
}
`;
}

function generateRouter(pages: Page[]): string {
  const imports = pages
    .map((p) => `import { ${pageComponentName(p)} } from "./pages/${pageComponentName(p)}";`)
    .join("\n");
  const routes = pages
    .map((p) => `        <Route path="${p.route || "/"}" element={<${pageComponentName(p)} />} />`)
    .join("\n");

  return `import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
${imports}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
${routes}
        <Route path="*" element={<Navigate to="${pages[0]?.route || "/"}" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
`;
}

function generateMockData(schema: AppSchema): string {
  const resources = Array.from(
    new Set(
      schema.pages
        .flatMap((p) => collectResources(p.layout))
        .filter(Boolean),
    ),
  );

  const entries = resources.map((r) => {
    const model = schema.dataModels.find((m) => m.tableName === r || m.name === r);
    const sample: Record<string, unknown> = {};
    if (model) {
      model.fields.forEach((f) => {
        if (f.name === "id") sample[f.name] = "00000000-0000-0000-0000-000000000001";
        else if (f.type === "boolean") sample[f.name] = true;
        else if (f.type === "integer" || f.type === "number") sample[f.name] = 1;
        else if (f.type === "date" || f.type === "datetime") sample[f.name] = new Date().toISOString();
        else sample[f.name] = `${f.name} 示例`;
      });
    } else {
      sample.name = `${r} 示例`;
      sample.status = "active";
    }
    return `  '${r}': [${JSON.stringify(sample)}]`;
  });

  return `export const mockData: Record<string, Record<string, unknown>[]> = {\n${entries.join(",\n")}\n};\n`;
}

function collectResources(node: ComponentNode): string[] {
  const resources: string[] = [];
  if ((node.type === "Table" || node.type === "Form") && node.props?.resource) {
    resources.push(String(node.props.resource));
  }
  node.children?.forEach((c) => resources.push(...collectResources(c)));
  return resources;
}

export function generateProjectFiles(schema: AppSchema): Record<string, string> {
  const files: Record<string, string> = {};

  files["package.json"] = JSON.stringify(
    {
      name: schema.name?.toLowerCase().replace(/\s+/g, "-") || "exported-app",
      version: schema.version || "0.1.0",
      private: true,
      type: "module",
      scripts: {
        dev: "vite",
        build: "tsc && vite build",
        preview: "vite preview",
      },
      dependencies: {
        "react": "^18.3.1",
        "react-dom": "^18.3.1",
        "react-router-dom": "^6.26.0",
      },
      devDependencies: {
        "@types/react": "^18.3.0",
        "@types/react-dom": "^18.3.0",
        "@vitejs/plugin-react": "^4.3.0",
        typescript: "^5.5.4",
        vite: "^5.3.5",
      },
    },
    null,
    2,
  );

  files["vite.config.ts"] = `import { defineConfig } from "vite";
import react from "@vitejs/plugin-react";

export default defineConfig({
  plugins: [react()],
});
`;

  files["tsconfig.json"] = JSON.stringify(
    {
      compilerOptions: {
        target: "ES2022",
        lib: ["ES2022", "DOM", "DOM.Iterable"],
        module: "ESNext",
        skipLibCheck: true,
        moduleResolution: "bundler",
        allowImportingTsExtensions: true,
        resolveJsonModule: true,
        isolatedModules: true,
        noEmit: true,
        jsx: "react-jsx",
        strict: true,
        noUnusedLocals: true,
        noUnusedParameters: true,
        noFallthroughCasesInSwitch: true,
      },
      include: ["src"],
    },
    null,
    2,
  );

  files["index.html"] = `<!doctype html>
<html lang="zh-CN">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${schema.name || "App"}</title>
    <script src="https://cdn.tailwindcss.com"></script>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.tsx"></script>
  </body>
</html>
`;

  files["src/main.tsx"] = `import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
`;

  files["src/App.tsx"] = generateRouter(schema.pages);
  files["src/data/mock.ts"] = generateMockData(schema);

  schema.pages.forEach((page) => {
    files[`src/pages/${pageComponentName(page)}.tsx`] = generatePageComponent(page);
  });

  return files;
}

export async function downloadProjectZip(schema: AppSchema): Promise<void> {
  const files = generateProjectFiles(schema);
  const zip = new JSZip();
  Object.entries(files).forEach(([path, content]) => {
    zip.file(path, content);
  });
  const blob = await zip.generateAsync({ type: "blob" });
  saveAs(blob, `${schema.name?.toLowerCase().replace(/\s+/g, "-") || "app"}.zip`);
}
